import { apiClient } from './client';
import {
  FlowResponse, FlowSummary, CreateFlowRequest, UpdateFlowRequest, UpdateScheduleRequest,
  FlowRevisionResponse, FlowRevisionDetailResponse, PublishFlowRequest,
  WebhookTokenResponse,
} from '../types';

export const getFlows = () =>
  apiClient.get<FlowSummary[]>('/api/flows').then(r => r.data);

export const getFlow = (id: string) =>
  apiClient.get<FlowResponse>(`/api/flows/${id}`).then(r => r.data);

export const createFlow = (req: CreateFlowRequest) =>
  apiClient.post<FlowResponse>('/api/flows', req).then(r => r.data);

export const updateFlow = (id: string, req: UpdateFlowRequest) =>
  apiClient.put<FlowResponse>(`/api/flows/${id}`, req).then(r => r.data);

/** The only call that changes what runs in production. */
export const publishFlow = (id: string, req: PublishFlowRequest) =>
  apiClient.post<FlowResponse>(`/api/flows/${id}/publish`, req).then(r => r.data);

export const addFavorite = (id: string) =>
  apiClient.put(`/api/flows/${id}/favorite`).then(() => {});

export const removeFavorite = (id: string) =>
  apiClient.delete(`/api/flows/${id}/favorite`).then(() => {});

export const deleteFlow = (id: string) =>
  apiClient.delete(`/api/flows/${id}`).then(() => {});

export const activateFlow = (id: string) =>
  apiClient.patch<FlowResponse>(`/api/flows/${id}/activate`).then(r => r.data);

export const deactivateFlow = (id: string) =>
  apiClient.patch<FlowResponse>(`/api/flows/${id}/deactivate`).then(r => r.data);

export const exportFlow = (id: string) =>
  apiClient.get(`/api/flows/${id}/export`).then(r => r.data);

export const updateFlowSchedule = (id: string, req: UpdateScheduleRequest) =>
  apiClient.patch<FlowResponse>(`/api/flows/${id}/schedule`, req).then(r => r.data);

/** Switches the webhook to token auth and returns the token — shown once, never stored. */
export const createWebhookToken = (id: string) =>
  apiClient.post<WebhookTokenResponse>(`/api/flows/${id}/webhook-token`).then(r => r.data);

export const deleteWebhookToken = (id: string) =>
  apiClient.delete(`/api/flows/${id}/webhook-token`).then(() => {});

/** Takes effect without a publish, once the trigger cache refreshes (up to 30 s). */
export const setWebhookAlias = (id: string, alias: string) =>
  apiClient.put<FlowResponse>(`/api/flows/${id}/webhook-alias`, { alias }).then(r => r.data);

export const deleteWebhookAlias = (id: string) =>
  apiClient.delete(`/api/flows/${id}/webhook-alias`).then(() => {});

/** Endpoint config like the alias: not versioned, takes effect without a publish. */
export const setApiRunEnabled = (id: string, enabled: boolean) =>
  apiClient.put<FlowResponse>(`/api/flows/${id}/api-run`, { enabled }).then(r => r.data);

export const importFlow = (body: string) =>
  apiClient.post<FlowResponse>('/api/flows/import', body, {
    headers: { 'Content-Type': 'application/json' },
  }).then(r => r.data);

export const listRevisions = (flowId: string) =>
  apiClient.get<FlowRevisionResponse[]>(`/api/flows/${flowId}/revisions`).then(r => r.data);

export const getRevision = (flowId: string, revisionId: string) =>
  apiClient.get<FlowRevisionDetailResponse>(`/api/flows/${flowId}/revisions/${revisionId}`).then(r => r.data);

/** Loads a revision into the draft. Does not affect what is live — publish does that. */
export const restoreRevision = (flowId: string, revisionId: string) =>
  apiClient.post<FlowResponse>(`/api/flows/${flowId}/revisions/${revisionId}/restore`).then(r => r.data);
